import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play, TrendingDown, TrendingUp } from "lucide-react";
import ItfChart from "@/components/ItfChart";
import { scrollToId } from "@/lib/api";

const HEADLINE = ["See the market", "the way institutions", "move it."];

export default function Hero() {
    const cardRef = useRef(null);
    const [tilt, setTilt] = useState({ rx: 0, ry: 0 });
    const { scrollY } = useScroll();
    const parallaxY = useTransform(scrollY, [0, 700], [0, 70]);

    const onMove = (e) => {
        const r = cardRef.current?.getBoundingClientRect();
        if (!r) return;
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        setTilt({ rx: -py * 3.5, ry: px * 3.5 });
    };

    return (
        <section className="relative overflow-hidden pt-28 sm:pt-36" data-testid="hero-section">
            <div className="hero-grid-bg absolute inset-0" />
            <div className="pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-emerald-100/50 blur-3xl" />
            <div className="pointer-events-none absolute right-[-160px] top-40 h-[380px] w-[380px] rounded-full bg-indigo-100/40 blur-3xl" />

            <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 pb-20 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8">
                <div className="lg:col-span-5">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="mb-6 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 shadow-sm"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse-dot" />
                        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600">
                            TradingView Indicator
                        </span>
                    </motion.div>

                    <h1 className="font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-slate-900 sm:text-5xl lg:text-[3.6rem]">
                        {HEADLINE.map((line, i) => (
                            <span key={line} className="block overflow-hidden pb-0.5">
                                <motion.span
                                    className={`block ${i === 2 ? "text-emerald-600" : ""}`}
                                    initial={{ y: "112%" }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    {line}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.65 }}
                        className="mt-6 max-w-md text-base leading-relaxed text-slate-600 sm:text-lg"
                    >
                        ITF analyzes institutional trading activity, market behavior and price structure — then draws
                        it as <span className="font-semibold text-slate-900">one clean line</span> on your chart.
                        Green means bullish flow. Red means bearish flow. That is the whole idea.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.8 }}
                        className="mt-8 flex flex-wrap items-center gap-4"
                    >
                        <Link
                            to="/checkout"
                            data-testid="hero-cta-primary-button"
                            className="group flex items-center gap-2 rounded-full bg-slate-900 px-7 py-3.5 text-base font-semibold text-white shadow-lg transition-all duration-200 hover:scale-[1.03] hover:bg-slate-800 hover:shadow-xl active:scale-[0.98]"
                        >
                            Get ITF — $275/year
                            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                        </Link>
                        <button
                            onClick={() => scrollToId("#demo")}
                            data-testid="hero-cta-demo-button"
                            className="group flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3.5 text-base font-semibold text-slate-700 transition-all duration-200 hover:scale-[1.02] hover:border-slate-400 hover:text-slate-900 active:scale-[0.98]"
                        >
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 transition-colors group-hover:bg-emerald-200">
                                <Play className="ml-0.5 h-3 w-3 fill-current" />
                            </span>
                            Watch it in action
                        </button>
                    </motion.div>

                </div>

                <motion.div className="relative lg:col-span-7" style={{ y: parallaxY }}>
                    <motion.div
                        initial={{ opacity: 0, y: 40, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <div
                            ref={cardRef}
                            onMouseMove={onMove}
                            onMouseLeave={() => setTilt({ rx: 0, ry: 0 })}
                            style={{
                                transform: `perspective(1200px) rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)`,
                                transition: "transform 0.25s ease-out",
                            }}
                            className="chart-card-shadow relative rounded-2xl border border-slate-200/80 bg-white"
                            data-testid="hero-chart-card"
                        >
                            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
                                <div className="flex items-center gap-3">
                                    <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse-dot" />
                                    <span className="font-display text-sm font-bold tracking-tight text-slate-900">LIVE DEMO</span>
                                    <span className="hidden text-xs text-slate-500 sm:block">Simulated market session</span>
                                    <span className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-[10px] font-semibold text-slate-600">5m</span>
                                </div>
                                <div className="flex items-center gap-4 font-mono text-[10px] font-semibold uppercase tracking-wider">
                                    <span className="flex items-center gap-1.5 text-emerald-600">
                                        <span className="inline-block h-[3px] w-4 rounded-full bg-emerald-600" /> Bullish
                                    </span>
                                    <span className="flex items-center gap-1.5 text-rose-600">
                                        <span className="inline-block h-[3px] w-4 rounded-full bg-rose-600" /> Bearish
                                    </span>
                                </div>
                            </div>
                            <div className="px-2 py-2">
                                <ItfChart timeframe="5M" height={380} startDelay={900} />
                            </div>
                            <div className="border-t border-slate-100 px-5 py-3">
                                <p className="text-xs text-slate-500">
                                    <span className="font-semibold text-emerald-700">Green line below price</span> — bullish flow in control
                                    <span className="mx-2 text-slate-300">·</span>
                                    <span className="font-semibold text-rose-700">Red line above price</span> — bearish flow in control
                                </p>
                            </div>
                        </div>

                        <motion.div
                            animate={{ y: [0, -8, 0] }}
                            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                            className="absolute -left-3 top-16 hidden rounded-xl border border-slate-200/80 bg-white/90 px-4 py-3 shadow-lg backdrop-blur-md md:block lg:-left-8"
                        >
                            <div className="flex items-center gap-2.5">
                                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                                    <TrendingUp className="h-4 w-4" />
                                </span>
                                <div>
                                    <p className="font-display text-xs font-bold text-slate-900">Direction change</p>
                                    <p className="font-mono text-[10px] text-slate-500">flow turned bullish</p>
                                </div>
                            </div>
                        </motion.div>
                        <motion.div
                            animate={{ y: [0, 9, 0] }}
                            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
                            className="absolute -right-3 bottom-24 hidden rounded-xl border border-slate-200/80 bg-white/90 px-4 py-3 shadow-lg backdrop-blur-md md:block lg:-right-6"
                        >
                            <div className="flex items-center gap-2.5">
                                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-100 text-rose-600">
                                    <TrendingDown className="h-4 w-4" />
                                </span>
                                <div>
                                    <p className="font-display text-xs font-bold text-slate-900">Direction change</p>
                                    <p className="font-mono text-[10px] text-slate-500">flow turned bearish</p>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}
